// =============================================================================
// CHAIN GENERATOR — src/chainGenerator.ts
// Builds ChainUpgrade[] arrays from structural step definitions.
// Auto-generates costs and descriptions; apply powerMult from balance.ts.
//
// Do NOT edit this file for balance tuning — edit src/balance.ts instead.
// =============================================================================

import { ChainUpgrade, ChainStepDef } from './types';
import { CHAIN_CONFIG, CHAIN_COST_SCALE, CHAIN_COST_GLOBAL_MULT } from './balance';

// Shared cost calculator
function stepCost(chainName: string, stepIndex: number, override?: number): number {
  if (override != null) return override;
  const cfg = CHAIN_CONFIG[chainName];
  if (!cfg) return 100;
  const mult = (cfg.costMult ?? 1.0) * CHAIN_COST_GLOBAL_MULT;
  return Math.round(cfg.firstStepCost * Math.pow(CHAIN_COST_SCALE, stepIndex) * mult);
}

// ---- buildDamageChain ----------------------------------------------------------
// For all standard damage-dealing units (rock_thrower, archer, club_swinger, etc.)
// Reads step.damageBonus, scales by powerMult, auto-generates "+X% damage" description prefix.
// step.suffix is appended: "+50% damage, +1 pierce"
// step.noDamage: true → no damage output, description is just the suffix.
//
export function buildDamageChain(chainName: string, steps: ChainStepDef[]): ChainUpgrade[] {
  const cfg = CHAIN_CONFIG[chainName] ?? { powerMult: 1.0, firstStepCost: 100 };

  return steps.map((step, i) => {
    const cost = stepCost(chainName, i, step.costOverride);
    let description: string;
    const effect: Record<string, unknown> = { ...step.effect };

    if (step.noDamage || step.damageBonus == null) {
      description = step.suffix ?? '';
    } else {
      const scaled = step.damageBonus * cfg.powerMult;
      const pct = Math.round(scaled * 100);
      description = step.suffix ? `+${pct}% damage, ${step.suffix}` : `+${pct}% damage`;
      effect.damageMultBonus = scaled;
    }

    return { id: step.id, name: step.name, description, cost, effect } as ChainUpgrade;
  });
}

// ---- buildRepairChain ----------------------------------------------------------
// For the builder unit.
// Reads step.repairMult (e.g. 1.5 = +50%), scales the bonus by powerMult.
// With powerMult=0.8: repairMult 1.5 becomes 1 + (0.5 × 0.8) = 1.4 (+40%).
// step.noDamage: true → no repairRateMult output (pure utility step like -30% interval).
//
export function buildRepairChain(chainName: string, steps: ChainStepDef[]): ChainUpgrade[] {
  const cfg = CHAIN_CONFIG[chainName] ?? { powerMult: 1.0, firstStepCost: 100 };

  return steps.map((step, i) => {
    const cost = stepCost(chainName, i, step.costOverride);
    let description: string;
    const effect: Record<string, unknown> = { ...step.effect };

    if (step.noDamage || step.repairMult == null) {
      description = step.suffix ?? '';
    } else {
      const scaledBonus = (step.repairMult - 1) * cfg.powerMult;
      const pct = Math.round(scaledBonus * 100);
      description = step.suffix ? `+${pct}% repair rate, ${step.suffix}` : `+${pct}% repair rate`;
      effect.repairRateMult = 1 + scaledBonus;
    }

    return { id: step.id, name: step.name, description, cost, effect } as ChainUpgrade;
  });
}

// ---- buildSwarmChain -----------------------------------------------------------
// For the beehive thrower.
// Reads step.swarmMult (e.g. 1.7 = +70% swarm DPS), scales the bonus by powerMult.
// step.noDamage: true → no swarmDpsMult output (pure utility step like +30% radius).
//
export function buildSwarmChain(chainName: string, steps: ChainStepDef[]): ChainUpgrade[] {
  const cfg = CHAIN_CONFIG[chainName] ?? { powerMult: 1.0, firstStepCost: 100 };

  return steps.map((step, i) => {
    const cost = stepCost(chainName, i, step.costOverride);
    let description: string;
    const effect: Record<string, unknown> = { ...step.effect };

    if (step.noDamage || step.swarmMult == null) {
      description = step.suffix ?? '';
    } else {
      const scaledBonus = (step.swarmMult - 1) * cfg.powerMult;
      const pct = Math.round(scaledBonus * 100);
      description = step.suffix ? `+${pct}% swarm DPS, ${step.suffix}` : `+${pct}% swarm DPS`;
      effect.swarmDpsMult = 1 + scaledBonus;
    }

    return { id: step.id, name: step.name, description, cost, effect } as ChainUpgrade;
  });
}
